import React, { useCallback } from 'react'
import classNames from 'classnames'
import { Else, If, Then, When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { CaptionSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { saveGuildContinueWatching } from '@/services/GuildContinueWatching'
import { useGuildMember } from '@/services/GuildUserService'
import { ContentWithWatchableReasons, isStreaming } from '@/services/ProjectPhaseService'
import { Episode } from '@/services/ProjectsService'
import { Trailer } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { GuildEmailInput } from '@/views/GuildSignupView/GuildEmailInput'

interface LoginToWatchButtonGroupProps {
  episode?: Episode | null
  isGuildMemberContent: boolean
  slug: string
  trailer?: Trailer
  isLoggedIn?: boolean
  watchableContent?: ContentWithWatchableReasons | null
}

function areDatesEqual(date1?: Date, date2?: Date) {
  if (!date1 || !date2) return false
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}
export const LoginToWatchButtonGroup: React.FC<LoginToWatchButtonGroupProps> = ({
  episode,
  isGuildMemberContent,
  slug,
  trailer,
  isLoggedIn = false,
  watchableContent,
}) => {
  const now = new Date()
  const earlyAccessAvailableDate = episode?.earlyAccessDate ? new Date(episode?.earlyAccessDate) : undefined
  const publiclyAvailableDate = episode?.publiclyAvailableDate ? new Date(episode?.publiclyAvailableDate) : undefined
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const { isGuildMember } = useGuildMember()
  const track = useSafeTrack()

  const handleJoinToWatchClicked = useCallback(() => {
    track('Clicked Join to Watch', { episode })
    if (episode && isStreaming(watchableContent)) saveGuildContinueWatching(episode, track, locale)
  }, [episode, track, locale, watchableContent])

  const availableDateTime = areDatesEqual(earlyAccessAvailableDate, new Date())
    ? Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(earlyAccessAvailableDate)
    : earlyAccessAvailableDate?.toLocaleDateString()

  const publiclyAvailableDateTime = areDatesEqual(publiclyAvailableDate, new Date())
    ? Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(publiclyAvailableDate)
    : publiclyAvailableDate?.toLocaleDateString()

  const isInEarlyAccess = earlyAccessAvailableDate && earlyAccessAvailableDate <= now
  const isEarlyAccessInFuture = earlyAccessAvailableDate && earlyAccessAvailableDate >= now
  const isInPublicAvailability = publiclyAvailableDate && publiclyAvailableDate < now
  const isPublicAvailabilityInFuture = publiclyAvailableDate && publiclyAvailableDate >= now

  return (
    <div className={classNames('flex flex-col md:flex-row', { 'w-full': !isGuildMemberContent && !isGuildMember })}>
      <When condition={isGuildMemberContent && !isGuildMember}>
        <If condition={isInEarlyAccess}>
          <Then>
            <LinkButton
              className="box-border h-8 min-h-[50px] w-full rounded-md backdrop-blur-sm md:w-fit"
              href={`${paths.guild.join}/${slug}`}
              onClick={handleJoinToWatchClicked}
              variant="white"
            >
              <div className="mr-2">
                <GuildIconMono size={22} color={'core-gray-950'} />
              </div>
              <CaptionSM weight="semibold" className="text-base">
                <When condition={isLoggedIn}>{t('getAccess', 'Get Access')}</When>
                <When condition={!isLoggedIn}>{t('unlockToWatch', 'Unlock to Watch')}</When>
              </CaptionSM>
            </LinkButton>
          </Then>
          <Else>
            <Button
              className="box-border h-8 min-h-[50px] w-full cursor-not-allowed rounded-md backdrop-blur-sm md:w-fit"
              variant="white"
            >
              <div className="mr-2">
                <GuildIcon size={24} />
              </div>
              <CaptionSM weight="bold" className="text-base">
                {t('availableDate', 'Available {{date}}', { date: availableDateTime })}
              </CaptionSM>
            </Button>
          </Else>
        </If>
      </When>
      <When condition={isGuildMemberContent && isGuildMember && isEarlyAccessInFuture}>
        <Button
          className="box-border h-8 min-h-[50px] w-full cursor-not-allowed rounded-md backdrop-blur-sm md:w-fit"
          variant="white"
        >
          <CaptionSM weight="bold" className="text-base">
            {t('availableDate', 'Available {{date}}', { date: availableDateTime })}
          </CaptionSM>
        </Button>
      </When>
      <When condition={!isGuildMemberContent && !isGuildMember}>
        <If condition={isPublicAvailabilityInFuture}>
          <Then>
            <Button
              className="box-border h-8 min-h-[50px] w-full cursor-not-allowed rounded-md backdrop-blur-sm md:w-fit"
              variant="white"
            >
              <CaptionSM weight="bold" className="text-base">
                {t('availableDate', 'Available {{date}}', { date: publiclyAvailableDateTime })}
              </CaptionSM>
            </Button>
          </Then>
          <Else>
            <When condition={isInPublicAvailability}>
              <div className="w-full">
                <GuildEmailInput
                  variant="left-white"
                  loginOrigin="episode"
                  hasUser={isLoggedIn}
                  isCentered={false}
                  isHome
                />
              </div>
            </When>
          </Else>
        </If>
      </When>
      <When condition={Boolean(trailer?.id)}>
        <LinkButton
          href={`/watch/${slug}/videos/trailer/${trailer?.id}`}
          variant="white"
          outline
          className="mt-4 box-border h-8 min-h-[50px] w-full rounded-md backdrop-blur-sm md:ml-4 md:mt-0 md:w-fit"
        >
          <CaptionSM weight="bold" className="text-base">
            {t('watchTrailer', 'Watch Trailer')}
          </CaptionSM>
        </LinkButton>
      </When>
    </div>
  )
}
